import { useEffect, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useMigration } from '@/lib/migration/useMigration'
import { SubmissionShell } from '@/components/migration/MigrationShell'
import { PlanRail, type RailSelection } from '@/components/migration/PlanRail'
import { AnnexForm } from '@/components/migration/AnnexForm'
import { PartAForm } from '@/components/migration/PartAForm'
import { VersionsModal } from '@/components/migration/ReviewExportModal'
import { Icon } from '@/components/readiness/primitives'
import { timeAgo } from '@/lib/readiness/util'

// Submission plan workspace (I3 §10, canvas 3B): page header, the 300px rail,
// and one annex (or Part A) at a time. Route /readiness/submission/plan with
// `?annex={id}` or `?part=a`; `?add={system_id}` adds that system's annex.

export default function SubmissionPlan() {
  const ctx = useMigration()
  const { plan: data, actions, summary, locked } = ctx
  const [params, setParams] = useSearchParams()
  const [showVersions, setShowVersions] = useState(false)
  const [showCsa, setShowCsa] = useState(false)

  const annexes = data.annexes
  const annexParam = params.get('annex')
  const partParam = params.get('part')
  const addParam = params.get('add')
  const selection: RailSelection = partParam === 'a' || (!annexParam && annexes.length === 0) ? { part: 'a', section: window.location.hash.replace('#', '') || undefined } : { annex: annexParam && annexes.some((a) => a.id === annexParam) ? annexParam : (annexes.find((a) => !a.archived_at) ?? annexes[0])?.id ?? '' }

  const select = (s: RailSelection) => {
    const p = new URLSearchParams(params)
    p.delete('annex')
    p.delete('part')
    p.delete('add')
    if ('part' in s) p.set('part', 'a')
    else p.set('annex', s.annex)
    setParams(p, { replace: true })
    if ('part' in s && s.section) window.setTimeout(() => document.getElementById(`parta-${s.section}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50)
    else window.scrollTo({ top: 0 })
  }

  // Deep link from a blocking check: add the annex for that CII system.
  const adding = useRef<string | null>(null)
  useEffect(() => {
    if (!addParam || !data.plan || adding.current === addParam) return
    const existing = annexes.find((a) => a.system_id === addParam)
    if (existing) {
      select({ annex: existing.id })
      return
    }
    adding.current = addParam
    void actions.addAnnexFromSystem(addParam).then((a) => select({ annex: a.id })).catch(() => select({ part: 'a' }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addParam, data.plan?.id])

  const current = 'annex' in selection ? annexes.find((a) => a.id === selection.annex) ?? null : null
  // Prev/Next and "n of m" run over Part B, or over the archived list when an archived annex is open.
  const siblings = annexes.filter((a) => !!a.archived_at === !!current?.archived_at)
  const idx = current ? siblings.indexOf(current) : -1
  const lastTouched = data.plan ? [data.plan.updated_at, ...annexes.map((a) => a.updated_at), ...annexes.flatMap((a) => a.blocks.map((b) => b.updated_at))].sort().pop() ?? null : null

  return (
    <SubmissionShell
      ctx={ctx}
      testId="submission-plan"
      right={
        summary && (
          <span className="flex flex-wrap items-center gap-2 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>
            <span data-testid="autosaved">{locked ? 'Read-only' : `Autosaved ${lastTouched ? timeAgo(lastTouched) : ''}`} · <span className="rd-mono">{summary.completeness_pct}%</span> complete</span>
            <button type="button" className="rd-btn" onClick={() => setShowVersions(true)} data-testid="versions"><Icon name="history" size={14} /> Versions <span className="rd-mono" title={`${data.versions.length} saved ${data.versions.length === 1 ? 'copy' : 'copies'}`}>{new Set(data.versions.map((v) => v.plan_version ?? 1)).size}</span></button>
          </span>
        )
      }
    >
      {data.plan && summary && (
        <>
          <div className="mb-3 flex flex-wrap items-center gap-2">
            <h2 className="rd-section-title">CSA quantum-safe migration plan</h2>
            <span className="text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>· {summary.annex_count} / {summary.cii_count} CII systems with an annex · Part A {summary.part_a.answered} / 6</span>
          </div>
          {/* A submitted version is read-only through every route (§7.2). */}
          <fieldset disabled={locked} className="flex min-w-0 flex-col gap-4 lg:flex-row lg:items-start" data-testid="plan-workspace" aria-disabled={locked}>
            <PlanRail ctx={ctx} selected={selection} onSelect={select} />
            {'part' in selection ? (
              <PartAForm ctx={ctx} scrollTo={selection.section} showCsa={showCsa} onToggleCsa={() => setShowCsa((v) => !v)} />
            ) : current ? (
              <AnnexForm key={current.id} ctx={ctx} annex={current} index={idx} total={siblings.length} prev={siblings[idx - 1] ?? null} next={siblings[idx + 1] ?? null} onNavigate={(id) => select({ annex: id })} onRemoved={() => select(annexes.filter((a) => a.id !== current.id)[0] ? { annex: annexes.filter((a) => a.id !== current.id)[0].id } : { part: 'a' })} showCsa={showCsa} onToggleCsa={() => setShowCsa((v) => !v)} />
            ) : (
              <div className="rd-card flex-1 p-8 text-center text-[14px]" style={{ color: 'var(--rd-text-muted)' }}>Pick an annex in the rail, or <button type="button" className="rd-link" onClick={() => select({ part: 'a' })}>open Part A</button>.</div>
            )}
          </fieldset>
          <p className="mt-4 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>Changing an answer here never changes an objective's level. Section 1 and the asset rows follow Discovery; a value you type stays yours until you revert it.</p>
        </>
      )}
      {!data.plan && data.error && <div className="rd-card p-8 text-center text-[14px]" style={{ color: 'var(--rd-status-warn)' }}>The submission plan could not be loaded from qtz-readiness · {data.error.message}</div>}
      {showVersions && <VersionsModal ctx={ctx} onClose={() => setShowVersions(false)} />}
    </SubmissionShell>
  )
}
